import { ReactNode } from 'react';

import { initFilters as smsInitFilters } from '../../core/portal/pages/sms/billing/filters';
import { initFilters as ussdInitFilters } from '../../core/portal/pages/ussd/billing/filters';
import { initFilters as kycInitFilters } from '../../core/portal/pages/kyc/billing/filters';
import { initFilters as ondemandInitFilters } from '../../core/portal/pages/ondemand/billing/filters';
import { initFilters as payoutInitFilters } from '../../core/portal/pages/payout/billing/filters';
import { initFilters as airtimeInitFilters } from '../../core/portal/pages/airtime-and-data/billing/filters';
import { initFilters as operatorLookupInitFilters } from '../../core/portal/pages/operator-lookup/billing/filters';
import { initFilters as merchantPaymentInitFilters } from '../../core/portal/pages/merchant-payment/billing/filters';
import { initFilters as accountTransfersInitFilters } from '../../core/portal/pages/account-transfers/billing/filters';
// eslint-disable-next-line max-len
import { initFilters as customerAuthenticationInitFilters } from '../../core/portal/pages/customer-authentication/billing/filters';
// eslint-disable-next-line max-len
import { initFilters as customerEnrollmentInitFilters } from '../../core/portal/pages/customer-enrollment/billing/filters';
import { initFilters as wapInitFilters } from '../../core/portal/pages/wap/billing/filters';
import { initFilters as voiceInitFilters } from '../../core/portal/pages/voice/billing/filters';
import { FiltersContext, FiltersContextProps, FiltersValues, UpdateFiltersProps } from './filtersContext';
import { NORTADA_LAST_USER } from '../../../constants/storage';

import { generateKeyForLocalStorageFilters } from './utils';

interface Props {
  children: ReactNode;
}

const filtersTimeoutInMillis: number = 86_400_000; // 1 day in millis
export const initialFiltersValues: FiltersValues = {
  voice: {
    billing: voiceInitFilters
  },
  wap: {
    billing: wapInitFilters
  },
  ussd: {
    billing: ussdInitFilters
  },
  sms: {
    billing: smsInitFilters
  },
  operatorLookup: {
    billing: operatorLookupInitFilters
  },
  airtime: {
    billing: airtimeInitFilters
  },
  accountTransfers: {
    billing: accountTransfersInitFilters
  },
  ondemand: {
    billing: ondemandInitFilters
  },
  payout: {
    billing: payoutInitFilters
  },
  merchantPayment: {
    billing: merchantPaymentInitFilters
  },
  kyc: {
    billing: kycInitFilters
  },
  customerAuthentication: {
    billing: customerAuthenticationInitFilters
  },
  customerEnrollment: {
    billing: customerEnrollmentInitFilters
  }
};

interface SessionStoreEntry {
  filterValues: FiltersValues;
  date?: Date;
}

export const WithFilters = ({ children }: Props) => {
  const userName = sessionStorage.getItem(NORTADA_LAST_USER);
  const getFiltersFromLocalStorage = () => {
    if (userName) {
      const maybeSessionStoreEntry = localStorage.getItem(generateKeyForLocalStorageFilters(userName));
      if (maybeSessionStoreEntry) {
        const sessionStoreEntry: SessionStoreEntry = JSON.parse(maybeSessionStoreEntry) as SessionStoreEntry;
        const now = new Date();
        if (sessionStoreEntry !== null && sessionStoreEntry.date !== undefined) {
          if (
            new Date(now.getTime() - new Date(sessionStoreEntry.date).getTime()).getMilliseconds() <
            filtersTimeoutInMillis
          ) {
            return sessionStoreEntry.filterValues;
          }
        }
      }
    }
    return initialFiltersValues;
  };

  const filtersContextProps: FiltersContextProps = {
    filtersValues: getFiltersFromLocalStorage(),
    handleUpdateFilters: ({ filtersToUpdatePath, newFilters }: UpdateFiltersProps) => {
      const getLoggedUserFiltersName = generateKeyForLocalStorageFilters(userName || 'user');

      filtersContextProps.filtersValues[filtersToUpdatePath].billing = newFilters;

      const data: SessionStoreEntry = {
        filterValues: filtersContextProps.filtersValues,
        date: new Date()
      };

      localStorage.setItem(getLoggedUserFiltersName, JSON.stringify(data));
    }
  };

  return <FiltersContext.Provider value={filtersContextProps}>{children}</FiltersContext.Provider>;
};
